import { useState, useEffect } from 'react';
import * as storage from '../utils/storage';

/**
 * Manages streak-related state and the streak warning polling effect.
 *
 * @param {boolean} isDataLoaded - Gate so effects don't run before initial storage load
 */
export const useStreak = (isDataLoaded) => {
  const [streak, setStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [recoveryMode, setRecoveryMode] = useState(false);
  const [showStreakWarning, setShowStreakWarning] = useState(false);
  const [showStreakDeath, setShowStreakDeath] = useState(false);
  const [showStreakSafe, setShowStreakSafe] = useState(false);

  // Get time until streak breaks (48 hours from last completion)
  const getStreakWarningStatus = () => {
    const lastCompletion = storage.getLastCompletion();
    if (!lastCompletion || streak === 0) return null;
    const now = Date.now();
    const timeSinceCompletion = now - lastCompletion;
    const fortyEightHours = 48 * 60 * 60 * 1000;
    const timeRemaining = fortyEightHours - timeSinceCompletion;

    if (timeRemaining <= 0) return null; // Streak already broken

    const hoursRemaining = Math.floor(timeRemaining / (60 * 60 * 1000));
    const minutesRemaining = Math.floor((timeRemaining % (60 * 60 * 1000)) / (60 * 1000));

    // Show warning if less than 12 hours remaining
    const shouldWarn = hoursRemaining < 12;
    const isCritical = hoursRemaining < 6;

    return { hoursRemaining, minutesRemaining, shouldWarn, isCritical, timeRemaining };
  };

  // Check streak warning status periodically
  useEffect(() => {
    if (!isDataLoaded) return;

    const checkStreakWarning = () => {
      const status = getStreakWarningStatus();
      setShowStreakWarning(status?.shouldWarn || false);
    };

    checkStreakWarning();
    const interval = setInterval(checkStreakWarning, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [streak, isDataLoaded]);

  return {
    streak, setStreak,
    longestStreak, setLongestStreak,
    recoveryMode, setRecoveryMode,
    showStreakWarning, setShowStreakWarning,
    showStreakDeath, setShowStreakDeath,
    showStreakSafe, setShowStreakSafe,
    getStreakWarningStatus,
  };
};
